import { forwardRef, useId } from 'react';
import type { InputHTMLAttributes, TextareaHTMLAttributes } from 'react';

interface CommonProps {
  label: string;
  error?: string | undefined;
  hint?: string | undefined;
}

type TextFieldProps = CommonProps & InputHTMLAttributes<HTMLInputElement>;
type TextAreaProps = CommonProps & TextareaHTMLAttributes<HTMLTextAreaElement>;

/**
 * Ein Eingabefeld mit Beschriftung und Fehlerhinweis.
 *
 * **`forwardRef` ist hier Pflicht, nicht Kuer.** React Hook Form registriert
 * ein Feld ueber dessen `ref`. Ohne Weiterleitung verwirft React die `ref`
 * mit einer Warnung -- das Formular liest dann keinen einzigen Wert aus, und
 * das Absenden schickt leere Felder an den Server. Der Fehler ist im Betrieb
 * schwer zu erkennen, weil die Oberflaeche vollstaendig richtig aussieht.
 *
 * Die Verknuepfung von Beschriftung, Feld und Fehlermeldung ueber `id` und
 * `aria-describedby` ist ebenfalls keine Formsache: Ohne sie liest ein
 * Bildschirmleser ein unbeschriftetes Feld vor, und die Fehlermeldung
 * erreicht den Nutzer gar nicht.
 */
export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { label, error, hint, ...rest },
  ref,
) {
  const id = useId();
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;

  const describedBy = [error ? errorId : null, hint ? hintId : null].filter(Boolean).join(' ');

  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      <input
        ref={ref}
        id={id}
        className="field__control"
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={describedBy || undefined}
        {...rest}
      />
      {hint ? (
        <span className="field__hint" id={hintId}>
          {hint}
        </span>
      ) : null}
      {error ? (
        <span className="field__error" id={errorId} role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
});

export const TextAreaField = forwardRef<HTMLTextAreaElement, TextAreaProps>(function TextAreaField(
  { label, error, hint, ...rest },
  ref,
) {
  const id = useId();
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;

  const describedBy = [error ? errorId : null, hint ? hintId : null].filter(Boolean).join(' ');

  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      <textarea
        ref={ref}
        id={id}
        className="field__control"
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={describedBy || undefined}
        {...rest}
      />
      {hint ? (
        <span className="field__hint" id={hintId}>
          {hint}
        </span>
      ) : null}
      {error ? (
        <span className="field__error" id={errorId} role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
});
